import { ComponentProps, FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty } from 'lodash/fp';

import { FieldLabel, FormGroup, RangeSlider } from '@portal/common/components';
import { YEAR_KEY } from '@portal/common/models/data-key';
import { DataGranularityKey } from '@portal/common/types';
import sortTimeUnits from '@portal/common/utility/data/time-units/sort-time-units';
import {
    firstAndLastFromArray,
    getLocalePrefixForGranularityKey,
    minMaxTupleToRangeArray,
} from '@portal/common/utility/filters-helpers';

import _ from '../../locale';
import { validateSelectedRefinementFiltersComplexityLimit } from '../../store/data-explorer/actions';
import {
    getNowcastYears,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
} from '../../store/data-explorer/selectors';
import { appendNowcastYearsToYearOptions } from '../../utility/data/nowcasts';

type Props = ComponentProps<typeof RangeSlider> & {
    id?: string;
    styles?: object;
    label?: string;
    localePrefix?: string;
    labelStyles?: object;
    filterKey: DataGranularityKey;
    preprocessChange?: (value: any) => any;
    onChangeComplete?: (value: any) => any;
    filterValue?: number[];
    undefinedWhenAllSelected?: boolean;
    isLockedMinValue?: boolean;
    shouldIncludeNowcasts?: boolean;
};

const ChartRangeSlider: FC<Props> = ({
    id,
    className,
    label,
    filterKey,
    preprocessChange,
    labelStyles,
    style,
    onChangeComplete,
    undefinedWhenAllSelected,
    isLockedMinValue,
    shouldIncludeNowcasts = false,
    ...props
}) => {
    const dispatch = useDispatch();

    const granularity = useSelector(getSelectedConditionsDataTypesGranularity);
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const nowcastYears = useSelector(getNowcastYears);

    const localePrefix = props.localePrefix || getLocalePrefixForGranularityKey(filterKey);

    let options =
        (refinedGranularity != null ? refinedGranularity[filterKey] : granularity[filterKey]) || [];

    options = sortTimeUnits(options, filterKey);

    if (
        filterKey === YEAR_KEY &&
        shouldIncludeNowcasts &&
        !isEmpty(options) &&
        !isEmpty(nowcastYears)
    ) {
        options = appendNowcastYearsToYearOptions(options, nowcastYears);
    }

    const indexToFilterValue = (index: number) => options[index];

    const filterValue = props.filterValue;
    const setFilterValue = (value) => {
        dispatch(validateSelectedRefinementFiltersComplexityLimit({ [filterKey]: value }));
    };

    const [sliderValue, setSliderValue] = useState<[number, number] | null>(null);

    const [minIndex, maxIndex] = firstAndLastFromArray(options.map((v, idx) => idx)) || [0, 0];

    const handleSliderChange = useCallback(
        (value: [number, number] | null) => {
            if (value) {
                const minValue = isLockedMinValue ? minIndex : value[0];
                const maxValue = value[1];

                if (minValue == minIndex && maxValue == maxIndex) {
                    setSliderValue(null);
                } else {
                    setSliderValue([minValue, maxValue]);
                }
            }
        },
        [minIndex, maxIndex]
    );

    const filterToSliderValue = (filterValue?: number[]) => {
        const values = filterValue;
        if (values) {
            return values.map((v) => options.indexOf(v));
        }

        return filterValue;
    };

    const [selectedStartIndex, selectedEndIndex] = firstAndLastFromArray(
        filterToSliderValue(filterValue)
    ) || [0, 0];

    // sync internal sliderValue with external filterValue
    useEffect(() => {
        if (sliderValue != null && filterValue == null) {
            setSliderValue(null);
        } else if (sliderValue == null && filterValue != null) {
            setSliderValue([selectedStartIndex, selectedEndIndex]);
        }
    }, [selectedStartIndex, selectedEndIndex]);

    return (
        <FormGroup className={className} style={{ padding: '0px 12px 16px', marginBottom: 30 }}>
            {label && (
                <FieldLabel style={{ marginLeft: -12, ...labelStyles }} htmlFor={id}>
                    {label}
                </FieldLabel>
            )}
            <RangeSlider
                id={id}
                data-testid={id}
                min={minIndex}
                max={maxIndex}
                marks={
                    maxIndex
                        ? {
                              [minIndex]: _.withPrefix(localePrefix, indexToFilterValue(minIndex)),
                              [maxIndex]: _.withPrefix(localePrefix, indexToFilterValue(maxIndex)),
                          }
                        : undefined
                }
                tipFormatter={(index) => _.withPrefix(localePrefix, indexToFilterValue(index))}
                value={sliderValue || [minIndex, maxIndex]}
                onChange={handleSliderChange}
                onChangeComplete={(value: [number, number]) => {
                    // preserve current value when new value empty
                    if (!value || !setFilterValue) {
                        return;
                    }

                    if (undefinedWhenAllSelected) {
                        const [startIndex, endIndex] = value;
                        if (startIndex === minIndex && endIndex === maxIndex) {
                            setFilterValue(undefined);

                            return;
                        }
                    }

                    const rangeArrayToFilterValue = (rangeValue: number[] | null) => {
                        if (rangeValue == null) {
                            return rangeValue;
                        }

                        return rangeValue.map(indexToFilterValue);
                    };
                    let filterValue = rangeArrayToFilterValue(minMaxTupleToRangeArray(value));

                    if (preprocessChange) {
                        filterValue = preprocessChange(filterValue);
                    }

                    setFilterValue(filterValue);
                    onChangeComplete?.(filterValue);
                }}
                {...(isLockedMinValue && {
                    handleStyle: [{ display: 'none' }, {}],
                    dotStyle: { pointerEvents: 'none' },
                })}
                style={style}
                {...props}
            />
        </FormGroup>
    );
};

export default ChartRangeSlider;
